import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { interviewAPI } from '../../utils/api';
import { toast } from '../../hooks/use-toast';
import { Video, Mic, Send, StopCircle, MessageCircle } from 'lucide-react';

const InterviewSession = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userResponse, setUserResponse] = useState('');
  const [sending, setSending] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [currentFeedback, setCurrentFeedback] = useState(null);

  useEffect(() => {
    loadSession();
    startVideo();
    return () => stopVideo();
  }, [sessionId]);

  const loadSession = async () => {
    try {
      const data = await interviewAPI.getSession(sessionId);
      setSession(data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load interview session',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const startVideo = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: true, 
        audio: true 
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      
      // Setup media recorder
      mediaRecorderRef.current = new MediaRecorder(stream);
      setIsRecording(true);
    } catch (error) {
      toast({
        title: 'Camera Access Required',
        description: 'Please allow camera and microphone access',
        variant: 'destructive',
      });
    }
  };

  const stopVideo = () => {
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
    }
    setIsRecording(false);
  };

  const startSpeechRecognition = () => {
    if (!('webkitSpeechRecognition' in window)) {
      toast({
        title: 'Not Supported',
        description: 'Speech recognition not supported in this browser',
        variant: 'destructive',
      });
      return;
    }

    const recognition = new window.webkitSpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.onresult = (event) => {
      let transcript = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      setUserResponse(transcript);
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognition.start();
    setIsListening(true);
  };

  const handleSendResponse = async () => {
    if (!userResponse.trim()) {
      toast({
        title: 'Error',
        description: 'Please provide a response',
        variant: 'destructive',
      });
      return;
    }

    setSending(true);
    try {
      const response = await interviewAPI.sendMessage(sessionId, userResponse);
      
      // Update session with new messages
      await loadSession();
      
      // Show feedback
      setCurrentFeedback(response.feedback);
      
      // Clear input
      setUserResponse('');
      
      toast({
        title: 'Response Sent',
        description: 'AI is analyzing your answer...',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to send response',
        variant: 'destructive',
      });
    } finally {
      setSending(false);
    }
  };

  const handleCompleteInterview = async () => {
    try {
      await interviewAPI.completeInterview(sessionId);
      stopVideo();
      navigate(`/interview/feedback/${sessionId}`);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to complete interview',
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 flex items-center justify-center">
        <div className="text-xl text-gray-700">Loading interview session...</div>
      </div>
    );
  }

  const latestAIMessage = session?.transcript?.filter(m => m.role === 'ai').slice(-1)[0];

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
              Live Interview Session
            </h1>
            <p className="text-gray-600 mt-1">Practice your visa interview with AI</p>
          </div>
          <Badge className="bg-red-500 text-white animate-pulse">
            <StopCircle className="h-4 w-4 mr-1" />
            Recording
          </Badge>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Video & Interview */}
          <div className="lg:col-span-2 space-y-6">
            {/* Video Feed */}
            <Card>
              <CardContent className="p-4">
                <div className="relative bg-black rounded-lg overflow-hidden" style={{ aspectRatio: '16/9' }}>
                  <video
                    ref={videoRef}
                    autoPlay
                    muted
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute bottom-4 left-4 right-4 flex justify-center space-x-4">
                    <Button
                      variant={isRecording ? 'destructive' : 'default'}
                      size="lg"
                      className="rounded-full"
                    >
                      <Video className="h-5 w-5" />
                    </Button>
                    <Button
                      variant={isListening ? 'default' : 'outline'}
                      size="lg"
                      className="rounded-full"
                      onClick={startSpeechRecognition}
                    >
                      <Mic className="h-5 w-5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* AI Question */}
            <Card className="bg-gradient-to-br from-blue-50 to-cyan-50 border-2 border-blue-200">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <MessageCircle className="h-5 w-5 mr-2 text-blue-600" />
                  Interviewer
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-lg text-gray-800">
                  {latestAIMessage?.message || 'Waiting for AI response...'}
                </p>
              </CardContent>
            </Card>

            {/* User Response */}
            <Card>
              <CardHeader>
                <CardTitle>Your Response</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Textarea
                  value={userResponse}
                  onChange={(e) => setUserResponse(e.target.value)}
                  placeholder="Type your response here or use the microphone button..."
                  rows={6}
                  className="text-lg"
                />
                <div className="flex space-x-3">
                  <Button
                    onClick={handleSendResponse}
                    disabled={sending || !userResponse.trim()}
                    className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    {sending ? 'Sending...' : 'Send Response'}
                  </Button>
                  <Button
                    onClick={handleCompleteInterview}
                    variant="outline"
                  >
                    Complete Interview
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - Feedback & Tips */}
          <div className="space-y-6">
            {/* Real-time Feedback */}
            {currentFeedback && (
              <Card className="bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-200">
                <CardHeader>
                  <CardTitle className="text-lg">Real-time Feedback</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div>
                    <p className="font-semibold text-gray-700">Content Quality:</p>
                    <p className="text-gray-600">{currentFeedback.content_quality}</p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-700">Confidence Score:</p>
                    <div className="flex items-center">
                      <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2">
                        <div
                          className="bg-gradient-to-r from-emerald-500 to-teal-500 h-2 rounded-full"
                          style={{ width: `${currentFeedback.confidence_score * 10}%` }}
                        />
                      </div>
                      <span className="font-semibold">{currentFeedback.confidence_score}/10</span>
                    </div>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-700">Tips:</p>
                    <p className="text-gray-600">{currentFeedback.speaking_tips}</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Behavioral Tips */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Behavioral Tips</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-gray-700">
                <p>✓ Maintain eye contact with camera</p>
                <p>✓ Sit upright with good posture</p>
                <p>✓ Speak clearly and at moderate pace</p>
                <p>✓ Use natural hand gestures</p>
                <p>✓ Show confidence in your answers</p>
              </CardContent>
            </Card>

            {/* Transcript */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Interview Transcript</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {session?.transcript?.map((msg, index) => (
                    <div
                      key={index}
                      className={`p-3 rounded-lg ${
                        msg.role === 'ai'
                          ? 'bg-blue-50 border-l-4 border-blue-500'
                          : 'bg-green-50 border-l-4 border-green-500'
                      }`}
                    >
                      <p className="text-xs font-semibold text-gray-600 mb-1">
                        {msg.role === 'ai' ? 'Interviewer' : 'You'}
                      </p>
                      <p className="text-sm text-gray-800">{msg.message}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InterviewSession;